import { ReactNode, Suspense, lazy } from 'react';
import { Link } from 'react-router';
import { useSnapshot } from 'valtio';

import * as authStore from '@cartwhl/common/stores/authStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getStudioUrl } from '@cartwhl/common/utils';
import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';
import HeaderSubMenu from '@cartwhl/ui/layout/HeaderSubMenu';
import MobileMenu from '@cartwhl/ui/layout/MobileMenu';
import UserMenu from '@cartwhl/ui/organisms/UserMenu';
import FrostedGlass from '@cartwhl/ui/utilities/FrostedGlass';

const AnimatedLogo = lazy(() => import('@cartwhl/ui/atoms/AnimatedLogo'));

const navLinks = [
  { label: 'Data', path: '/data' },
  // TODO: Re-enable Blog when ready
  // { label: 'Blog', path: '/blog' },
  { label: 'Company', path: '/company' },
];

const productsMenuItems = [
  {
    title: 'API',
    subtitle: 'Access at scale',
    href: '/product/api-access',
    comingSoonText: '',
  },
  {
    title: 'Comic',
    subtitle: 'AI video mocap',
    href: '/product/comic-ai-video-motion-capture',
    comingSoonText: '',
  },
  {
    title: 'Swing',
    subtitle: 'Text-to-motion',
    href: '/product/swing-motion-generation',
    comingSoonText: '',
  },
  {
    title: 'Hermes',
    subtitle: 'Motion blending & editing',
    href: '/product/hermes-smart-in-between',
    comingSoonText: '',
  },
  {
    title: 'Pose Mode',
    subtitle: 'Pose-to-render',
    href: '/product/pose-render',
    comingSoonText: '',
  },
  {
    title: 'Character Generator',
    subtitle: 'Reference-to-rigged 3D',
    href: '/product/character-generation',
    comingSoonText: '',
  },
  {
    title: 'Motion Library',
    subtitle: 'Production-ready motion',
    href: '/product/motion-library',
    comingSoonText: '',
  },
  {
    title: 'Cartographer',
    subtitle: 'Universal motion retargeting',
    href: '/product/cartographer',
    comingSoonText: 'Closed beta',
  },
  {
    title: 'Props',
    subtitle: 'Object motion capture',
    href: '/product/capture-transfer-object-motion',
    comingSoonText: 'Coming soon',
  },
];

const pricingMenuItems = [
  {
    title: 'Canvas Pricing',
    subtitle: 'See plans for Canvas',
    href: '/canvas-pricing',
  },
  { title: 'API Pricing', subtitle: 'See plans for API', href: '/api-pricing' },
];

const learnMenuItems = [
  {
    title: 'Canvas Docs',
    subtitle: 'Learn how to use Canvas',
    href: 'https://docs.getcartwheel.com/docs/getting-started/introduction',
  },
  {
    title: 'API Docs',
    subtitle: 'API reference & guides',
    href: 'https://api-docs.getcartwheel.com/',
  },
];

// Check if we're on the homepage origin (client-side nav works)
// vs another app like applets (need full page navigation)
const isOnHomepage = getHomepageUrl('/') === `${window.location.origin}/`;

interface HomepageLinkProps {
  to: string;
  className?: string;
  children: ReactNode;
}

function HomepageLink({ to, className, children }: HomepageLinkProps) {
  if (isOnHomepage) {
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={getHomepageUrl(to)} className={className}>
      {children}
    </a>
  );
}

interface HeaderProps {
  onApiAccessClick?: () => void;
  onComingSoonClick?: (slug?: string) => void;
}

export default function Header({
  onApiAccessClick,
  onComingSoonClick,
}: HeaderProps = {}) {
  const { authStatus } = useSnapshot(authStore.state);
  const { theme } = useSnapshot(userStore.state);
  const isSignedIn = authStatus === 'user';

  // Header is 128px with nav centered. Negative top value ensures nav sits at 16px from viewport when stuck
  return (
    <header className="sticky top-0 z-50 flex h-20 items-center justify-center px-2 lg:top-[-16px] lg:h-[128px] lg:px-5">
      <FrostedGlass className="container rounded-lg lg:rounded-full">
        <div className="flex h-16 items-center justify-between">
          <HomepageLink to="/" className="flex items-center">
            <Suspense fallback={null}>
              <AnimatedLogo theme={theme ?? 'light'} />
            </Suspense>
          </HomepageLink>

          {/* Mobile menu */}
          <MobileMenu
            productItems={productsMenuItems}
            pricingItems={pricingMenuItems}
            learnItems={learnMenuItems.map((item) => ({
              ...item,
              external: true,
            }))}
            onComingSoonClick={onComingSoonClick}
          />

          {/* Desktop navigation */}
          <nav className="hidden items-center gap-6 lg:flex">
            <HeaderSubMenu
              trigger={
                <Text
                  variant="web/body/medium/md"
                  className="transition-opacity hover:opacity-80"
                >
                  Product
                </Text>
              }
            >
              <HeaderSubMenu.Grid columns={[4, 3, 2]}>
                {productsMenuItems.map((item) => (
                  <HeaderSubMenu.Item
                    key={item.href}
                    title={item.title}
                    subtitle={item.subtitle}
                    href={item.href}
                    comingSoon={item.comingSoonText !== ''}
                    comingSoonText={item.comingSoonText}
                    onComingSoonClick={
                      item.comingSoonText !== '' ? onComingSoonClick : undefined
                    }
                  />
                ))}
              </HeaderSubMenu.Grid>
            </HeaderSubMenu>

            {navLinks.slice(0, 1).map((link) => (
              <HomepageLink
                key={link.label}
                to={link.path}
                className="transition-opacity hover:opacity-80"
              >
                <Text variant="web/body/medium/md">{link.label}</Text>
              </HomepageLink>
            ))}

            <HeaderSubMenu
              trigger={
                <Text
                  variant="web/body/medium/md"
                  className="transition-opacity hover:opacity-80"
                >
                  Pricing
                </Text>
              }
            >
              <HeaderSubMenu.Grid columns={1}>
                {pricingMenuItems.map((item) => (
                  <HeaderSubMenu.Item
                    key={item.href}
                    title={item.title}
                    subtitle={item.subtitle}
                    href={item.href}
                  />
                ))}
              </HeaderSubMenu.Grid>
            </HeaderSubMenu>

            {navLinks.slice(1).map((link) => (
              <HomepageLink
                key={link.label}
                to={link.path}
                className="transition-opacity hover:opacity-80"
              >
                <Text variant="web/body/medium/md">{link.label}</Text>
              </HomepageLink>
            ))}

            <HeaderSubMenu
              trigger={
                <Text
                  variant="web/body/medium/md"
                  className="transition-opacity hover:opacity-80"
                >
                  Learn
                </Text>
              }
            >
              <HeaderSubMenu.Grid columns={1}>
                {learnMenuItems.map((item) => (
                  <HeaderSubMenu.Item
                    key={item.href}
                    title={item.title}
                    subtitle={item.subtitle}
                    href={item.href}
                    external
                  />
                ))}
              </HeaderSubMenu.Grid>
            </HeaderSubMenu>

            <div className="flex items-center gap-2">
              {isSignedIn ? (
                <>
                  {onApiAccessClick && (
                    <Button variant="secondary" onClick={onApiAccessClick}>
                      API access
                    </Button>
                  )}
                </>
              ) : (
                <HomepageLink to="/login">
                  <Button variant="secondary">Sign in</Button>
                </HomepageLink>
              )}
              {isSignedIn ? (
                <a href={getStudioUrl()}>
                  <GoButton variant="animate">Open Canvas</GoButton>
                </a>
              ) : (
                <HomepageLink to="/sign-up">
                  <GoButton variant="animate">Try it free</GoButton>
                </HomepageLink>
              )}
              {isSignedIn && <UserMenu />}
            </div>
          </nav>
        </div>
      </FrostedGlass>
    </header>
  );
}
